import { config } from "../../../config";
import { UserMarketingProfileOfferMetricTypeEnum } from "./types/UserMarketingProfileOfferMetricTypeEnum";

// Regular properties
interface BaseUserMarketingProfileMetricsData {
  personalUserOfferId: string;
  userMarketingProfileId: string;
}

// Mapped type for metrics
type MetricsData = {
  [key in UserMarketingProfileOfferMetricTypeEnum]?: number;
};

// Combined type
type UserMarketingProfileOfferMetricsUpdateData =
  BaseUserMarketingProfileMetricsData & MetricsData;

/**
 * Updates marketing metrics for a user marketing profile offer
 * @param data The metrics data to update
 * @returns Promise that resolves when the update is complete
 */
export async function updateUserMarketingProfileOffersMetrics(
  data: UserMarketingProfileOfferMetricsUpdateData
): Promise<void> {
  // Check if any enum metric is present in data
  const hasMetricData = Object.values(
    UserMarketingProfileOfferMetricTypeEnum
  ).some(
    (metricKey) =>
      metricKey in data && data[metricKey as keyof typeof data] !== undefined
  );

  // Only log if metric data is present
  if (hasMetricData && config.env === "development") {
    console.log("🎯 UMPO Metrics Update Triggered:", {
      timestamp: new Date().toISOString(),
      data,
      endpoint: `${config.backend.base_url}/UserMarketingProfileOffer/UpdateUserMarketingProfileOffer`,
    });
  }

  try {
    const response = await fetch(
      `${config.backend.base_url}/UserMarketingProfileOffer/UpdateUserMarketingProfileOffer`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update metrics: ${response.statusText}`);
    }

    if (hasMetricData && config.env === "development") {
      console.log("✅ UMPO Metrics Update Successful:", {
        timestamp: new Date().toISOString(),
        response: response,
        status: response.status,
        data,
      });
    } else if (config.env === "development") {
      console.debug("Skipped logging - no metric data provided");
    }
  } catch (error) {
    if (hasMetricData && config.env === "development") {
      console.error("❌ User Marketing Profile Metrics Offer Update Failed:", {
        timestamp: new Date().toISOString(),
        error,
        data,
      });
    } else {
      console.error(
        "Error updating user marketing profile offer metrics:",
        error
      );
    }
    throw error;
  }
}
